// This page implements the original (V1) Unbox safety flow. A newer version
// now exists — see packages/frontend/src/pages/safety-beta.
import { useNavigate, useSearch } from "@tanstack/react-router";
import { AlertTriangle, CheckVerified02, RefreshCcw05, Shield01 } from "@untitledui/icons";
import { Tabs } from "@/components/application/tabs/tabs";
import type { legacyAllProcessedVideosRoute } from "@/router";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { SafetyVideosTable } from "./table";

const tabs = [
  { id: "risksDetected" as const, label: "Risks Detected", icon: <AlertTriangle className="w-4 h-4" /> },
  { id: "deemedSafe" as const, label: "Videos deemed safe", icon: <CheckVerified02 className="w-4 h-4" /> },
  { id: "unableToAnalyse" as const, label: "Risks unknown", icon: <Shield01 className="w-4 h-4" /> },
];

// Derive tab type from the tabs array
type TabId = (typeof tabs)[number]["id"];

export const AllProcessedVideos = () => {
  const urlParams = useSearch({ strict: false }) as ReturnType<(typeof legacyAllProcessedVideosRoute)["useSearch"]>;
  const { tab, video_id } = urlParams;
  const navigate = useNavigate() as ReturnType<(typeof legacyAllProcessedVideosRoute)["useNavigate"]>;

  return (
    <div className="flex flex-col w-full h-screen items-center">
      <div className={`${LAYOUT_CONSTRAINTS.CONTAINER_WIDTH} flex flex-col gap-5 h-full px-16 pt-16 pb-8`}>
        <div className="w-full h-full flex flex-row">
          <SafetyVideosTable
            category={tab}
            initialVideoId={video_id}
            onVideoIdChange={(videoId) => navigate({ search: (prev) => ({ ...prev, video_id: videoId }) })}
            navigate={navigate}
            currentSearch={urlParams}
            title={
              <div className="flex flex-col">
                <h1 className="text-xl font-normal text-primary flex flex-row items-center gap-2 mb-1">
                  <RefreshCcw05 className="w-5 h-5" strokeWidth={1.8} />
                  All Processed Videos
                </h1>
                <span className="text-quaternary text-sm">
                  Complete history of analysed videos and their classifications
                </span>
              </div>
            }
            tabs={
              <Tabs
                selectedKey={tab}
                onSelectionChange={(key) => {
                  navigate({ search: (prev) => ({ ...prev, tab: key as TabId }) });
                }}
                className="w-max max-md:hidden shrink-0"
              >
                <Tabs.List type="underline" items={tabs}>
                  {(tab) => <Tabs.Item {...tab} />}
                </Tabs.List>
              </Tabs>
            }
          />
        </div>
      </div>
    </div>
  );
};
